import { ApolloQueryResult } from 'apollo-client';
import { compact, get, isArray, mergeWith } from 'lodash';

import { VideoPreviewType } from '../../components/VideoPreview/VideoPreview';
import {
    CONTENT_PROMOTION_PREVIEW_DEFAULT_HEIGHT,
    CONTENT_PROMOTION_PREVIEW_DEFAULT_WIDTH,
    DEFAULT_SITELINKS_COUNT,
    EXTENDED_SITELINKS_COUNT
} from '../../constants';
import {
    BannerCriteriaKey,
    getBannerFilterCriteriaContainers,
    StatisticsByAllGoals
} from '../../declarations/filter/filter';
import {
    getBannerPriceData,
    getBannerTitleFallback,
    getImageFormatsFromApiForPcodeRenderDataImages,
    mapAdImageFormatsFragmentToBannerImageFormat,
    replaceTemplate
} from '../../service/BannerHelpers/BannerHelpers';
import { SerpPreviewHelpers } from '../../service/BannerPreviewHelpers/BannerPreviewHelpers';
import { flattenContainer, getStrictExpressions } from '../../service/FilterHelpers/FilterHelpers';
import { getUrlFromImageItem } from '../../service/ImageHelpers/ImageHelpers';
import {
    getAgeFromBannerModerationTheme,
    getWarningFromBannerModerationTheme
} from '../../service/Moderation/Moderation';
import { UrlHelpers } from '../../service/UrlHelpers';
import { BannerPrice } from '../../typings/banners';
import {
    bannerHasAge,
    bannerHasDynamicDisclaimer,
    bannerHasTitleAndBody,
    bannerHasWarning,
    EditedBanner,
    EditedBannerImage,
    Measurers
} from '../../typings/bannersEditState';
import { ClientInfo, Lang, OperatorModerationInfo } from '../../typings/common';
import {
    AdvType,
    AdvTypeClient,
    Banner,
    BannerImage,
    BannerModerationTheme,
    BannerPreviewData,
    BannerStatusForFilter,
    BannerVideo,
    CampaignsCalcTypeFilter,
    CampaignType,
    ClientData,
    PreviewType
} from '../../typings/domain';
import {
    BannersQueryFilter,
    Criteria,
    FilterExpression,
    FilterPartialExpression,
    GoalStatsQueryFilter,
    StatsQueryFilter
} from '../../typings/filter';
import {
    adGroupsRowFragmentForEdit,
    AdGroupTruncatedFragment,
    adPreviewFragment,
    adsPhraseRowFragment,
    adsPreviewRowFragment,
    adsRowFragment,
    adsRowFragment_GdInternalAd,
    adsRowFragment_GdTextAd,
    adsRowFragment_GdTextAd_typedCreative_GdSmartCreative,
    CalloutsIdsQuery,
    CurrencyCode,
    GdAdResources,
    GdAdType,
    GdAdTypeInput,
    GdBannerMeasurerInput,
    GdBannerMeasurerSystem,
    GdBannerMeasurerSystemInput,
    GdCampaignType,
    GdContentPromotionType,
    GdiRecommendationType,
    GdPixelInput,
    GdPixelKindInput
} from '../../typings/graphql-grid';
import { GridSourceParams } from '../../typings/grid';
import { BannerViewType, PcodeAdsImage, PcodeRenderData } from '../../typings/pcode';
import { PreviewData } from '../../typings/preview';
import { RecommendationCharacter, RecommendationsIconType } from '../../typings/recommendations';
import { SerpRenderData } from '../../typings/serp';
import { ClientFeatures } from '../../typings/state';
import { mapStrategyForPreviewBanner } from '../campaign';
import {
    getValueFromApiFormat,
    mapCampaignRowTypeToCampaignType,
    mapGroupRowTypeToGroupType,
    mapStatsDataToStats
} from '../common/common';
import { FilterCriteriaMappings, textToBannerFilterExpression } from '../filter/filter';
import { mapRowGoals } from '../goal/goal';
import { mapRowsToGroupsTruncatedBaseInfo } from '../group/group';
import {
    isRecommendationSupported,
    isRecommendationSupportedOnGridByAdsType,
    mapContextRecommendationFromApiFormat
} from '../recommendation';

const escapeHtml = require('escape-html');

export function mapRowsPhraseToBanners(
    bannerRows: adsPhraseRowFragment[],
    clientInfo: ClientData,
    params: GridSourceParams,
    group: AdGroupTruncatedFragment
): Banner[] {
    return bannerRows.map(row => {
        const bannerStatus = row.status;
        const recommendations = (row.recommendations || [])
            .filter(recommendation => isRecommendationSupported(recommendation.type))
            .map(mapContextRecommendationFromApiFormat);
        const rowGoalStats = row.goalStats || [];

        const bannerType = mapStringToAdvType(row.type);

        return {
            id: String(row.id),
            isEditAllow: row.access.canEdit,
            isAimingAllowed: false,
            exportId: '',
            bannerType,
            title: getValueFromApiFormat(row.title),
            titleExtension: getValueFromApiFormat(row.titleExtension),
            body: getValueFromApiFormat(row.body),
            group: mapRowsToGroupsTruncatedBaseInfo(group, clientInfo, params),
            domain: getValueFromApiFormat(row.domain),
            href: getBannerHrefFromApiFormat(row, group),
            status: {
                type: getValueFromApiFormat(bannerStatus.primaryStatus),
                isPreviousVersionShown: Boolean(bannerStatus.previousVersionShown),
                isRejectedOnModeration: Boolean(bannerStatus.rejectedOnModeration)
            },
            image: getBannerImageFromApiFormat(row, mapCampaignRowTypeToCampaignType(group.campaign.type), bannerType),
            video: getVideoFromApiFormat(row),
            contentPromotionUrl: getContentPromotionUrlFromApiFormat(row),
            stats: mapStatsDataToStats(row.stats),
            access: row.access,
            recommendations: {
                data: recommendations.length !== 0 ? recommendations : undefined,
                recommendationsIconType: recommendations.some(
                    recommendation => recommendation.character === RecommendationCharacter.attention
                )
                    ? RecommendationsIconType.red
                    : RecommendationsIconType.yellow
            },
            linkTail: getValueFromApiFormat(row.linkTail),
            dynamicDisclaimer: getValueFromApiFormat(row.dynamicDisclaimer),
            sitelinks: (row.sitelinks || []).map(el => getValueFromApiFormat(el.href) || ''),
            age: getAgeFromBannerModerationTheme((row.flags || []).filter(el => el) as BannerModerationTheme[]),
            warning: getWarningFromBannerModerationTheme((row.flags || []).filter(el => el) as BannerModerationTheme[]),
            operators: [],
            goalStats: mapRowGoals(rowGoalStats),
            aggregatedStatus: null
        };
    });
}

export function mapRowsToBanners(
    bannerRows: adsRowFragment[],
    clientInfo: ClientData,
    params: GridSourceParams,
    workCurrency: CurrencyCode,
    canvasDomain: string,
    placements: Record<string, string>
): Banner[] {
    return bannerRows.map(row => {
        const bannerStatus = row.status;
        const { aggregatedStatusInfo } = row;
        const recommendations = (row.recommendations || [])
            .filter(
                recommendation =>
                    isRecommendationSupported(recommendation.type) ||
                    isRecommendationSupportedOnGridByAdsType(recommendation.type, row.type)
            )
            .map(mapContextRecommendationFromApiFormat);
        const rowGoalStats = row.goalStats || [];
        const groupData = mapRowsToGroupsTruncatedBaseInfo(row.adGroup, clientInfo, params);

        const bannerType = mapStringToAdvType(row.type);
        const inactiveResources = row.status.hasInactiveResources.reduce(
            (res: GdAdResources[], resource: GdAdResources | null) => {
                if (resource !== null) {
                    res.push(resource);
                }

                return res;
            },
            []
        );

        return {
            id: String(row.id),
            isEditAllow: row.access.canEdit,
            isAimingAllowed: row.isAimingAllowed,
            exportId: String(row.exportId),
            bannerType,
            title: getValueFromApiFormat(row.title),
            titleExtension: getValueFromApiFormat(row.titleExtension),
            body: getValueFromApiFormat(row.body),
            group: groupData,
            domain: getValueFromApiFormat(row.domain),
            href: getBannerHrefFromApiFormat(row, row.adGroup),
            aggregatedStatusInfo:
                aggregatedStatusInfo !== null
                    ? {
                          status: aggregatedStatusInfo.status,
                          reasons: compact(aggregatedStatusInfo.reasons),
                          counters: {}
                      }
                    : undefined,
            aggregatedStatus: row.aggregatedStatus,
            status: {
                type: getValueFromApiFormat(bannerStatus.primaryStatus),
                isPreviousVersionShown: Boolean(bannerStatus.previousVersionShown),
                isRejectedOnModeration: Boolean(bannerStatus.rejectedOnModeration),
                isPlacementPagesRequired: Boolean(bannerStatus.placementPagesRequired),
                inactiveResources: getValueFromApiFormat(inactiveResources)
            },
            image: getBannerImageFromApiFormat(row, groupData.campaign.type, bannerType),
            video: getVideoFromApiFormat(row),
            stats: mapStatsDataToStats(row.stats),
            access: row.access,
            operators: ('placementPages' in row ? row.placementPages : []).map(
                ({ pageId, statusModerate, statusModerateOperator }): OperatorModerationInfo => {
                    return {
                        name: placements[pageId],
                        statusModerate,
                        statusModerateOperator
                    };
                }
            ),
            recommendations: {
                data: recommendations.length !== 0 ? recommendations : undefined,
                recommendationsIconType: recommendations.some(
                    recommendation => recommendation.character === RecommendationCharacter.attention
                )
                    ? RecommendationsIconType.red
                    : RecommendationsIconType.yellow
            },
            linkTail: getValueFromApiFormat(row.linkTail),
            dynamicDisclaimer: getValueFromApiFormat(row.dynamicDisclaimer),
            sitelinks: (row.sitelinks || []).map(el => getValueFromApiFormat(el.href) || ''),
            age: getAgeFromBannerModerationTheme((row.flags || []).filter(el => el) as BannerModerationTheme[]),
            warning: getWarningFromBannerModerationTheme((row.flags || []).filter(el => el) as BannerModerationTheme[]),
            bannerPrice: getPriceFromApiFormat(row as adsRowFragment_GdTextAd, workCurrency),
            goalStats: mapRowGoals(rowGoalStats),
            description: getDescriptionFromApiFormat(row as adsRowFragment_GdInternalAd),
            additionsFlags: {
                hasCallouts: row.hasCallouts,
                hasSitelink: row.hasSitelink,
                hasTurboGallery: row.hasTurboGallery,
                hasTurbolanding: row.hasTurbolanding,
                hasVideo: row.hasVideo
            },
            ...getLivePreviewData(canvasDomain, row),
            contentPromotionUrl: getContentPromotionUrlFromApiFormat(row),
            turbolandingHref: getTurbolandingHrefFromApiFormat(row)
        };
    });
}

export function getDescriptionFromApiFormat(row: adsRowFragment_GdInternalAd): string | undefined {
    return row.description || undefined;
}

export function getPriceFromApiFormat(
    row: adsRowFragment_GdTextAd,
    workCurrency: CurrencyCode
): BannerPrice | undefined {
    const bannerPrice = row.bannerPrice;
    return bannerPrice ? getBannerPriceData(bannerPrice, workCurrency) : undefined;
}

export function mapRowsToBannersPreviewData(
    bannerRows: adsPreviewRowFragment[],
    clientInfo: ClientData,
    params: GridSourceParams
): BannerPreviewData[] {
    return bannerRows.map((row: adsPreviewRowFragment) => {
        const bannerStatus = row.status;

        const bannerType = mapStringToAdvType(row.type);

        return {
            id: String(row.id),
            bannerType,
            title: getValueFromApiFormat(row.title),
            titleExtension: getValueFromApiFormat(row.titleExtension),
            body: getValueFromApiFormat(row.body),
            domain: getValueFromApiFormat(row.domain),
            group: mapRowsToGroupsTruncatedBaseInfo(row.adGroup, clientInfo, params),
            href: getBannerHrefFromApiFormat(row, row.adGroup),
            status: {
                type: getValueFromApiFormat(bannerStatus.primaryStatus),
                isPreviousVersionShown: Boolean(bannerStatus.previousVersionShown),
                isRejectedOnModeration: Boolean(bannerStatus.rejectedOnModeration)
            },
            image: getBannerImageFromApiFormat(
                row,
                mapCampaignRowTypeToCampaignType(row.adGroup.campaign.type),
                bannerType
            ),
            video: getVideoFromApiFormat(row),
            contentPromotionUrl: getContentPromotionUrlFromApiFormat(row),
            linkTail: getValueFromApiFormat(row.linkTail),
            dynamicDisclaimer: getValueFromApiFormat(row.dynamicDisclaimer),
            sitelinks: (row.sitelinks || []).map(el => getValueFromApiFormat(el.href) || ''),
            age: getAgeFromBannerModerationTheme((row.flags || []).filter(el => el) as BannerModerationTheme[]),
            warning: getWarningFromBannerModerationTheme((row.flags || []).filter(el => el) as BannerModerationTheme[])
        };
    });
}

export function mapRowGroupToBannerPreviewData(
    bannerRow: adPreviewFragment,
    campaignType: GdCampaignType,
    groupRow: adGroupsRowFragmentForEdit
): BannerPreviewData {
    const bannerType = mapStringToAdvType(bannerRow.type);

    return {
        id: String(bannerRow.id),
        bannerType,
        title: getValueFromApiFormat(bannerRow.title),
        group: {
            id: bannerRow.adGroupId,
            name: groupRow.name,
            campaign: {
                ...groupRow.campaign,
                type: mapCampaignRowTypeToCampaignType(groupRow.campaign.type),
                strategy: mapStrategyForPreviewBanner(groupRow.campaign.strategy)
            },
            access: groupRow.access,
            regionIds: groupRow.regionsInfo.regionIds,
            minusKeywords: groupRow.minusKeywords,
            type: mapGroupRowTypeToGroupType(groupRow.type)
        },
        titleExtension: getValueFromApiFormat(bannerRow.titleExtension),
        body: getValueFromApiFormat(bannerRow.body),
        domain: getValueFromApiFormat(bannerRow.domain),
        href: getValueFromApiFormat(bannerRow.href),
        image: getBannerImageFromApiFormat(bannerRow, mapCampaignRowTypeToCampaignType(campaignType), bannerType),
        video: getVideoFromApiFormat(bannerRow),
        linkTail: getValueFromApiFormat(bannerRow.linkTail),
        dynamicDisclaimer: getValueFromApiFormat(bannerRow.dynamicDisclaimer),
        sitelinks: (bannerRow.sitelinks || []).map(el => getValueFromApiFormat(el.href) || ''),
        age: getAgeFromBannerModerationTheme((bannerRow.flags || []).filter(el => el) as BannerModerationTheme[]),
        warning: getWarningFromBannerModerationTheme((bannerRow.flags || []).filter(
            el => el
        ) as BannerModerationTheme[])
    };
}

type SmartCreative = adsRowFragment_GdTextAd_typedCreative_GdSmartCreative;

export function getBannerImageFromApiFormat(
    row: adsRowFragment | adsPhraseRowFragment | adsPreviewRowFragment | adPreviewFragment,
    campaignType: CampaignType,
    bannerType: AdvType
): BannerImage | undefined {
    const { typedCreative: creative, bannerImage, type } = row;

    const image = row.image || row.bannerImage;

    if (row.__typename === 'GdContentPromotionAd') {
        return {
            height: CONTENT_PROMOTION_PREVIEW_DEFAULT_HEIGHT,
            width: CONTENT_PROMOTION_PREVIEW_DEFAULT_WIDTH,
            name: row.title ? replaceTemplate(row.title) : getBannerTitleFallback(bannerType),
            url: row.contentPromotionPreviewUrl || ''
        };
    }

    if (campaignType === CampaignType.PERFORMANCE || bannerType === AdvType.CPC_VIDEO || (!image && AdvType.IMAGE_AD)) {
        const duration = creative && 'duration' in creative ? creative.duration : undefined;
        let creativeLayoutName;
        let isAdaptive = false;
        if (type === GdAdType.PERFORMANCE) {
            const typedCreative = creative as SmartCreative;
            const { layout } = typedCreative;

            creativeLayoutName = layout && layout.name;
        }

        if (creative && creative.__typename === 'GdCanvasCreative') {
            isAdaptive = Boolean(creative.isAdaptive);
        }

        return creative !== null && creative.name !== null && creative.previewUrl !== null
            ? {
                  height: Number(creative.height),
                  width: Number(creative.width),
                  name: creativeLayoutName || creative.name,
                  isAdaptive,
                  duration,
                  url: creative.previewUrl
              }
            : undefined;
    }

    if (!image || !image.name || !image.imageSize.height || !image.imageSize.width) {
        return undefined;
    }

    return {
        height: Number(image.imageSize.height),
        width: Number(image.imageSize.width),
        name: image.name,
        url: getUrlFromImageItem(image.mdsGroupId, image.namespace, image.imageHash),
        formats:
            bannerImage && bannerImage.formats
                ? mapAdImageFormatsFragmentToBannerImageFormat(bannerImage.formats)
                : undefined
    };
}

export function getVideoFromApiFormat(
    row: adsRowFragment | adsPhraseRowFragment | adsPreviewRowFragment | adPreviewFragment
): BannerVideo | undefined {
    const { typedCreative: creative, type } = row;

    if (type === GdAdType.CPC_VIDEO && creative && creative.name && creative.previewUrl) {
        return {
            name: creative.name,
            url: creative.previewUrl
        };
    }

    return undefined;
}

export function mapStringToAdvType(advTypeString: string): AdvType {
    const advType = AdvType[advTypeString];

    if (!advType) {
        throw new Error(`Тип баннера ${advTypeString} не поддерживается`);
    }

    return advType;
}

export function mapStringToGdAdvTypeInput(advTypeString: string): GdAdTypeInput {
    const gdAdvType = GdAdTypeInput[advTypeString];

    if (!gdAdvType) {
        throw new Error(`Тип баннера ${advTypeString} не поддерживается`);
    }

    return gdAdvType;
}

export function mapPixelsToApiFormat(pixel: string, showsCounters: string[]): GdPixelInput[] {
    const mappedPixels = showsCounters.map(showsCounter => {
        if (showsCounter) {
            return {
                kind: GdPixelKindInput.AUDIT,
                url: showsCounter
            };
        }
        return false;
    });

    // Для корректной валидации ошибок важно, чтобы пиксель пушился последним.
    if (pixel) {
        mappedPixels.push({
            kind: GdPixelKindInput.AUDIENCE,
            url: pixel
        });
    }

    return compact(mappedPixels);
}

export function mapBannerMeasurerSystemToInput(system: GdBannerMeasurerSystem): GdBannerMeasurerSystemInput {
    const result = GdBannerMeasurerSystemInput[system];

    if (!result) {
        throw new Error(`Measurer system ${system} isn't supported`);
    }

    return result;
}

export function mapMeasurersToApiFormat(measurers: Measurers[]): GdBannerMeasurerInput[] {
    return measurers.map((measurer: Measurers) => {
        return {
            params: measurer.params,
            measurerSystem: mapBannerMeasurerSystemToInput(measurer.measurerSystem),
            hasIntegration: measurer.hasIntegration
        };
    });
}

const mappings = new FilterCriteriaMappings<BannersQueryFilter & StatsQueryFilter & GoalStatsQueryFilter>();
const { toNumber, toBoolean, toEnum } = mappings.valueMappings;

const bannerCriteriaMappings: Record<BannerCriteriaKey, (expression: FilterExpression) => BannersQueryFilter> = {
    id: mappings.includes('adIdIn', 'adIdNotIn'),
    primaryStatus: mappings.equality('primaryStatusContains', toEnum(BannerStatusForFilter)),
    types: mappings.equality('typeIn', toEnum(AdvType)),
    title: mappings.contains('titleContains', 'titleNotContains'),
    titleExtension: mappings.contains('titleExtensionContains', 'titleExtensionNotContains'),
    body: mappings.contains('bodyContains', 'bodyNotContains'),
    recommendations: mappings.equality('recommendations', toEnum(GdiRecommendationType)),
    hasImage: mappings.boolean('imageExists', toBoolean),
    hasSitelinks: mappings.boolean('sitelinksExists', toBoolean),
    hasTurbolanding: mappings.boolean('turbolandingsExist', toBoolean),

    cost: mappings.comparison('minCost', 'maxCost', toNumber),
    revenue: mappings.comparison('minRevenue', 'maxRevenue', toNumber),
    profitability: mappings.comparison('minProfitability', 'maxProfitability', toNumber),
    avgGoalCost: (expression: FilterExpression): BannersQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison(
                'minCostPerAction',
                'maxCostPerAction',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison('minAvgGoalCost', 'maxAvgGoalCost', toNumber)(expression);
    },
    avgClickCost: mappings.comparison('minAvgClickCost', 'maxAvgClickCost', toNumber),
    avgCpmCost: mappings.comparison('minCpmPrice', 'maxCpmPrice', toNumber),
    shows: mappings.comparison('minShows', 'maxShows', toNumber),
    clicks: mappings.comparison('minClicks', 'maxClicks', toNumber),
    ctr: mappings.comparison('minCtr', 'maxCtr', toNumber),
    goals: (expression: FilterExpression): BannersQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison('minGoals', 'maxGoals', toNumber, false, expression.additionalCriteriaKey)(
                expression
            );
        }

        return mappings.comparison('minGoals', 'maxGoals', toNumber)(expression);
    },
    conversionRate: (expression: FilterExpression): BannersQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison(
                'minConversionRate',
                'maxConversionRate',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison('minConversionRate', 'maxConversionRate', toNumber)(expression);
    },
    bounceRate: mappings.comparison('minBounceRate', 'maxBounceRate', toNumber)
};

const expressionToBannersQueryFilter = (expression: FilterExpression): BannersQueryFilter => {
    return bannerCriteriaMappings[expression.criteriaKey](expression);
};

export const mapBannerFilterToApiFormat = (
    expressions: FilterPartialExpression[],
    clientInfo: ClientInfo,
    features: ClientFeatures,
    calcType?: CampaignsCalcTypeFilter
): BannersQueryFilter => {
    const criteria: Criteria[] = [];

    getBannerFilterCriteriaContainers(clientInfo, features, calcType === CampaignsCalcTypeFilter.CPM).forEach(
        ({ criteria: c }) => criteria.push(...flattenContainer(c))
    );

    return getStrictExpressions(expressions, criteria, textToBannerFilterExpression)
        .map(expressionToBannersQueryFilter)
        .reduce(
            (obj, otherObj) =>
                mergeWith(obj, otherObj, (objValue, srcValue) => {
                    if (isArray(objValue)) {
                        return objValue.concat(srcValue);
                    }

                    return undefined;
                }),
            {}
        );
};

export const isBannerEditable = (banner: Banner): boolean => {
    return banner.access.canEdit;
};

export function getTurbolandingHrefFromApiFormat(banner: adsRowFragment): string | undefined {
    return banner.turbolanding !== null ? banner.turbolanding.href : undefined;
}

export function getBannerHrefFromApiFormat(
    banner: adsRowFragment | adsPhraseRowFragment | adsPreviewRowFragment,
    adGroup: AdGroupTruncatedFragment
): string | undefined {
    switch (adGroup.__typename) {
        case 'GdDynamicAdGroup':
            return getValueFromApiFormat(adGroup.mainDomain);
        case 'GdMobileContentAdGroup':
            return adGroup.storeHref;
        case 'GdContentPromotionAdGroup':
            if (banner.__typename !== 'GdContentPromotionAd') {
                return undefined;
            }
            return (
                getValueFromApiFormat(banner.contentPromotionVisitUrl) ||
                getValueFromApiFormat(banner.contentPromotionUrl)
            );
        default:
            return getValueFromApiFormat(banner.href);
    }
}

export function mapContentPromotionBannerToContentPromotionType(
    bannerType: AdvType | AdvTypeClient
): GdContentPromotionType {
    switch (bannerType) {
        case AdvType.CONTENT_PROMOTION_VIDEO:
            return GdContentPromotionType.VIDEO;
        case AdvType.CONTENT_PROMOTION_COLLECTION:
            return GdContentPromotionType.COLLECTION;
        default:
            throw new Error(`Banner type ${bannerType} is not supported`);
    }
}

function getContentPromotionUrlFromApiFormat(row: adsPreviewRowFragment | adsPhraseRowFragment): string | undefined {
    return row.__typename === 'GdContentPromotionAd' ? row.contentPromotionUrl : undefined;
}

export function mapPreviewDataToSerpRenderData(previewData: PreviewData, previewType: PreviewType): SerpRenderData {
    const isMobile = previewType === PreviewType.SEARCH_MOBILE;

    const url = escapeHtml(isMobile ? previewData.hrefMobile : previewData.href);

    const path = `${previewData.domain}/${previewData.displayHref}`;

    const substitution = SerpPreviewHelpers.getTitleAnBodyForPreview(
        isMobile,
        previewData.title,
        previewData.titleExtension,
        previewData.body,
        previewData.domain
    );
    const sitelinkCount = previewType === PreviewType.SEARCH_BNO ? EXTENDED_SITELINKS_COUNT : DEFAULT_SITELINKS_COUNT;

    return {
        add_info: {
            callouts_list: previewData.callouts,
            type: 'callouts'
        },
        age: previewData.age,
        bid: previewData.id,
        body: escapeHtml(substitution.body),
        domain: escapeHtml(previewData.domain),
        fav_domain: escapeHtml(previewData.domain),
        link_tail: previewData.linkTail,
        phone: escapeHtml(previewData.phone),
        position: '',
        search_prefs:
            previewType === PreviewType.SEARCH_BNO || previewType === PreviewType.SEARCH_MOBILE
                ? {
                      bno: '1',
                      show_favicon: '1'
                  }
                : '',
        sitelinks: (previewData.sitelinks || []).slice(0, sitelinkCount).map(el => {
            const link = el.protocol + el.link;
            const turbolink = get(el, 'turbo.href');

            const hasSite = Boolean(el.link);
            const hasTurboPage = Boolean(turbolink);

            let sitelinkUrl;
            if (isMobile) {
                sitelinkUrl = hasTurboPage ? turbolink : link;
            } else {
                sitelinkUrl = hasSite ? link : turbolink;
            }

            return {
                text: escapeHtml(el.descr),
                title: escapeHtml(el.title),
                url: escapeHtml(sitelinkUrl),
                no_redirect_url: escapeHtml(sitelinkUrl)
            };
        }),
        subtype: '',
        title: escapeHtml(substitution.title),
        url: url,
        path: escapeHtml(path),
        path_url: url,
        vcard_url: previewData.vcardUrl,
        warning: escapeHtml(previewData.dynamicDisclaimer || '') || previewData.warning,
        working_time: previewData.workingTime,
        region: previewData.region,
        subway_station: previewData.subwayStation,
        trafaret_serp_data: {
            SitelinkDescription: true,
            SitelinkCount: sitelinkCount
        }
    };
}

export function mapPreviewDataToPcodeRenderData(previewData: PreviewData): PcodeRenderData {
    return {
        type: BannerViewType.adaptive0418,
        ads: [
            {
                adId: previewData.id,
                title: previewData.title || '',
                body: previewData.body || '',
                url: previewData.href || '',
                domain: previewData.domain || '',
                punyDomain: previewData.domain || '',
                favicon: 'https://favicon.yandex.net/favicon/',
                vcardUrl: '',
                age: previewData.age || '',
                warning: previewData.dynamicDisclaimer || previewData.warning,
                sitelinks: (previewData.sitelinks || []).map(el => el.link || ''),
                dynamic_disclaimer: previewData.dynamicDisclaimer || '',
                banner_flags: '',
                lang: Lang.ru,
                linkTail: previewData.linkTail || '',
                images: previewData.image
                    ? previewData.image.formats
                        ? getImageFormatsFromApiForPcodeRenderDataImages(previewData.image.formats)
                        : [
                              [
                                  UrlHelpers.getAbsoluteImageUrl(previewData.image.url),
                                  previewData.image.height,
                                  previewData.image.width
                              ]
                          ]
                    : undefined
            }
        ]
    };
}

export function mapBannerToPcodeRenderData(
    banner: BannerPreviewData,
    withSize: boolean = false,
    type?: BannerViewType
): PcodeRenderData {
    const { image } = banner;
    let images: PcodeAdsImage[] | undefined;

    if (image) {
        if (image.formats) {
            images = getImageFormatsFromApiForPcodeRenderDataImages(image.formats);
        } else if (image.height && image.width) {
            images = [
                [
                    image.url.startsWith('http') ? image.url : UrlHelpers.getAbsoluteImageUrl(image.url),
                    image.height,
                    image.width
                ]
            ];
        }
    }

    return {
        // здесь такая глупая цепочка фолбеков из-за того, что для ТГО объявлений
        // с видеодополнением нужен разный previewType в зависимости от того,
        // хотим мы текстовый баннер или собственно видеодополнение
        type: type || banner.previewType || BannerViewType.adaptive0418,
        size: withSize && banner.image ? { height: banner.image.height, width: banner.image.width } : undefined,
        ads: [
            {
                adId: banner.id,
                title: banner.title || '',
                body: banner.body || '',
                url: banner.href || banner.turbolandingHref || '',
                domain: banner.domain || '',
                punyDomain: banner.domain || '',
                favicon: 'https://favicon.yandex.net/favicon/',
                vcardUrl: '',
                age: banner.age ? `${banner.age}+` : '',
                warning: banner.dynamicDisclaimer || banner.warning,
                sitelinks: banner.sitelinks,
                dynamic_disclaimer: banner.dynamicDisclaimer || '',
                banner_flags: '',
                lang: Lang.ru,
                linkTail: banner.linkTail || '',
                images
            }
        ],
        isSquareFormat: banner.bannerType === AdvType.CPM_AUDIO
    };
}

export const mapBannerEditDataToPcodeRenderData = (
    banner: EditedBanner,
    type: BannerViewType = BannerViewType.adaptive0418,
    image?: EditedBannerImage
): PcodeRenderData => {
    switch (banner.bannerType) {
        case AdvTypeClient.TEXT_MOBILE:
        case AdvType.IMAGE_AD:
        case AdvType.TEXT:
        case AdvType.CPM_OUTDOOR:
        case AdvType.CPM_INDOOR:
        case AdvType.CPM_VIDEO:
        case AdvType.CPM_AUDIO:
        case AdvType.CPC_VIDEO:
        case AdvType.CPM_BANNER:
        case AdvType.CPM_GEOPRODUCT:
            break;

        default:
            throw new Error(`некорректный тип баннера #${banner.id}: ${banner.bannerType}!`);
    }

    return {
        type,
        ads: [
            {
                adId: banner.id,
                title: bannerHasTitleAndBody(banner) ? banner.title : '',
                body: bannerHasTitleAndBody(banner) ? banner.body : '',
                url: get(banner, 'contacts.href', 'javascript:///void(0)'), // jshint ignore:line
                domain: get(banner, 'contacts.domain', ''),
                punyDomain: get(banner, 'contacts.domain', ''),
                favicon: 'https://favicon.yandex.net/favicon/',
                vcardUrl: '',
                age: bannerHasAge(banner) ? banner.age : '',
                warning: bannerHasWarning(banner) ? banner.warning : '',
                sitelinks: [],
                dynamic_disclaimer: (bannerHasDynamicDisclaimer(banner) && banner.dynamicDisclaimer) || '',
                banner_flags: '',
                lang: Lang.ru,
                linkTail: get(banner, 'contacts.displayHref', ''),
                images:
                    image && image.data && image.data.formats
                        ? getImageFormatsFromApiForPcodeRenderDataImages(image.data.formats)
                        : undefined
            }
        ],
        isSquareFormat: banner.bannerType === AdvType.CPM_AUDIO
    };
};

export const mapMassActionDataToPcodeRenderData = (
    type: BannerViewType = BannerViewType.adaptive0418
): PcodeRenderData => {
    return {
        type,
        ads: [
            {
                adId: '',
                title: '',
                body: '',
                url: 'javascript:///void(0)', // jshint ignore:line
                domain: '',
                punyDomain: '',
                favicon: 'https://favicon.yandex.net/favicon/',
                vcardUrl: '',
                age: '',
                warning: '',
                sitelinks: [],
                dynamic_disclaimer: '',
                banner_flags: '',
                lang: Lang.ru,
                linkTail: '',
                images: undefined
            }
        ]
    };
};

export const mapCalloutsIdsApiFormat = (result: ApolloQueryResult<CalloutsIdsQuery>): Array<string> => {
    const callouts = result.data.callouts;

    if (!callouts) {
        return [];
    }

    // TODO: DIRECT-88149 тайпинги бэка
    return callouts.map(item => (item ? item.id : ''));
};

const getLivePreviewData = (
    canvasDomain: string,
    banner: adsRowFragment
): { livePreviewUrl?: string; previewType?: BannerViewType } => {
    if (banner.typedCreative) {
        switch (banner.typedCreative.__typename) {
            case 'GdVideoAdditionCreative':
            case 'GdCpcVideoCreative':
            case 'GdCpmOutdoorCreative':
            case 'GdCpmIndoorCreative':
            case 'GdCpmVideoCreative':
            case 'GdCpmAudioCreative':
            case 'GdOverlayCreative':
                return {
                    livePreviewUrl:
                        'livePreviewUrl' in banner.typedCreative ? banner.typedCreative.livePreviewUrl : undefined,
                    previewType: BannerViewType.videoAdditionPreview
                };

            case 'GdCanvasCreative':
            case 'GdCpmGeoproductCreative':
                return {
                    livePreviewUrl: UrlHelpers.getCanvasCreativePreviewUrl(
                        canvasDomain,
                        banner.typedCreative.creativeId
                    ),
                    previewType: BannerViewType.adaptive0418
                };

            case 'GdHtml5Creative':
                return {
                    livePreviewUrl: UrlHelpers.getHTML5CreativePreviewUrl(
                        canvasDomain,
                        banner.typedCreative.creativeId
                    ),
                    previewType: BannerViewType.html5CreativePreview
                };

            case 'GdSmartCreative':
                return {
                    livePreviewUrl: banner.typedCreative.previewUrl,
                    previewType: BannerViewType.plainImagePreview
                };

            default:
                return {};
        }
    }
    if (banner.image && banner.image.formats && banner.image.formats.length) {
        return {
            livePreviewUrl: UrlHelpers.getAbsoluteImageUrlForPcode(banner.image.formats[0].path),
            previewType: BannerViewType.plainImagePreview
        };
    }
    return {};
};

export const mapAdvTypeToVideoPreviewType = (bannerType: AdvType | AdvTypeClient | undefined): VideoPreviewType => {
    switch (bannerType) {
        case AdvType.CPM_AUDIO:
            return VideoPreviewType.AUDIO;
        case AdvType.CPC_VIDEO:
        case AdvType.CPM_INDOOR:
        case AdvType.CPM_OUTDOOR:
        case AdvType.CPM_VIDEO:
        default:
            return VideoPreviewType.VIDEO;
    }
};
